import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Loading from './components/Loading';
import { PrivateRoute } from './components/PrivateRoute';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import PasswordResetForm from './components/PasswordResetForm';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import GitHubOAuth from './components/GitHubOAuth';
import LarkOAuth from './components/LarkOAuth';
import { API, getLogo, getSystemName, showError, showNotice } from './helpers';
import { UserContext } from './context/User';
import { StatusContext } from './context/Status';

const Channel = lazy(() => import('./pages/Channel'));
const EditChannel = lazy(() => import('./pages/Channel/EditChannel'));
const Token = lazy(() => import('./pages/Token'));
const EditToken = lazy(() => import('./pages/Token/EditToken'));
const Redemption = lazy(() => import('./pages/Redemption'));
const EditRedemption = lazy(() => import('./pages/Redemption/EditRedemption'));
const User = lazy(() => import('./pages/User'));
const EditUser = lazy(() => import('./pages/User/EditUser'));
const AddUser = lazy(() => import('./pages/User/AddUser'));
const Setting = lazy(() => import('./pages/Setting'));
const TopUp = lazy(() => import('./pages/TopUp'));
const Log = lazy(() => import('./pages/Log'));
const Chat = lazy(() => import('./pages/Chat'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Playground = lazy(() => import('./pages/Playground'));
const About = lazy(() => import('./pages/About'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  const [, userDispatch] = useContext(UserContext);
  const [statusState, statusDispatch] = useContext(StatusContext);

  const loadUser = () => {
    const user = localStorage.getItem('user');
    if (user) {
      userDispatch({ type: 'login', payload: JSON.parse(user) });
    }
  };

  const loadStatus = async () => {
    try {
      const res = await API.get('/api/status');
      const { success, message, data } = res.data || {};
      if (success && data) {
        localStorage.setItem('status', JSON.stringify(data));
        statusDispatch({ type: 'set', payload: data });
        localStorage.setItem('system_name', data.system_name);
        localStorage.setItem('logo', data.logo);
        localStorage.setItem('footer_html', data.footer_html);
        localStorage.setItem('quota_per_unit', data.quota_per_unit);
        localStorage.setItem('display_in_currency', data.display_in_currency);
        if (data.chat_link) {
          localStorage.setItem('chat_link', data.chat_link);
        } else {
          localStorage.removeItem('chat_link');
        }
        if (
          data.version !== process.env.REACT_APP_VERSION &&
          data.version !== 'v0.0.0' &&
          process.env.REACT_APP_VERSION !== ''
        ) {
          showNotice(
            `新版本可用：${data.version}，请使用快捷键 Shift + F5 刷新页面`
          );
        }
      } else {
        showError(message || '无法正常连接至服务器！');
      }
    } catch (error) {
      showError(error.message || '无法正常连接至服务器！');
    }
  };

  useEffect(() => {
    loadUser();
    loadStatus();
  }, []);

  useEffect(() => {
    const systemName = statusState.status?.system_name || getSystemName();
    const logo = statusState.status?.logo || getLogo();
    if (systemName) document.title = systemName;
    if (logo) {
      const linkElement = document.querySelector("link[rel~='icon']");
      if (linkElement) linkElement.href = logo;
    }
  }, [statusState.status]);

  const wrap = (node) => (
    <Suspense fallback={<Loading />}>{node}</Suspense>
  );

  return (
    <Routes>
      <Route
        path='/'
        element={
          localStorage.getItem('user') ? (
            <Navigate to='/token' replace />
          ) : (
            <Navigate to='/login' replace />
          )
        }
      />
      <Route path='/login' element={<LoginForm />} />
      <Route path='/register' element={<RegisterForm />} />
      <Route path='/reset' element={<PasswordResetForm />} />
      <Route path='/user/reset' element={wrap(<PasswordResetConfirm />)} />
      <Route path='/oauth/github' element={wrap(<GitHubOAuth />)} />
      <Route path='/oauth/lark' element={wrap(<LarkOAuth />)} />

      <Route path='/channel' element={<PrivateRoute>{wrap(<Channel />)}</PrivateRoute>} />
      <Route path='/channel/edit/:id' element={<PrivateRoute>{wrap(<EditChannel />)}</PrivateRoute>} />
      <Route path='/channel/add' element={<PrivateRoute>{wrap(<EditChannel />)}</PrivateRoute>} />
      <Route path='/token' element={<PrivateRoute>{wrap(<Token />)}</PrivateRoute>} />
      <Route path='/token/edit/:id' element={<PrivateRoute>{wrap(<EditToken />)}</PrivateRoute>} />
      <Route path='/token/add' element={<PrivateRoute>{wrap(<EditToken />)}</PrivateRoute>} />
      <Route path='/redemption' element={<PrivateRoute>{wrap(<Redemption />)}</PrivateRoute>} />
      <Route path='/redemption/edit/:id' element={<PrivateRoute>{wrap(<EditRedemption />)}</PrivateRoute>} />
      <Route path='/redemption/add' element={<PrivateRoute>{wrap(<EditRedemption />)}</PrivateRoute>} />
      <Route path='/user' element={<PrivateRoute>{wrap(<User />)}</PrivateRoute>} />
      <Route path='/user/edit/:id' element={<PrivateRoute>{wrap(<EditUser />)}</PrivateRoute>} />
      <Route path='/user/edit' element={<PrivateRoute>{wrap(<EditUser />)}</PrivateRoute>} />
      <Route path='/user/add' element={<PrivateRoute>{wrap(<AddUser />)}</PrivateRoute>} />
      <Route path='/setting' element={<PrivateRoute>{wrap(<Setting />)}</PrivateRoute>} />
      <Route path='/topup' element={<PrivateRoute>{wrap(<TopUp />)}</PrivateRoute>} />
      <Route path='/log' element={<PrivateRoute>{wrap(<Log />)}</PrivateRoute>} />
      <Route path='/chat' element={wrap(<Chat />)} />
      <Route path='/dashboard' element={<PrivateRoute>{wrap(<Dashboard />)}</PrivateRoute>} />
      <Route path='/playground' element={<PrivateRoute>{wrap(<Playground />)}</PrivateRoute>} />
      <Route path='/about' element={wrap(<About />)} />
      <Route path='*' element={wrap(<NotFound />)} />
    </Routes>
  );
}

export default App;
